import { Box, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useGetStudentsQuery } from "../../services/students.service";
import { CHIPS_COLOR } from "../../types/students";
import { useAppTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";

// Per-status accent colors for the progress bars
const STATUS_BAR_COLORS: Record<string, string> = {
  "initial": "#64748B",
  "visa": "#0EA5E9",
  "visa:reminder": "#EAB308",
  "visa:preparing": "#8B5CF6",
  "visa:submitted": "#059669",
  "admission:reminder": "#EAB308",
  "admission:preparing": "#8B5CF6",
  "admission:submitted": "#059669",
  "issue resolution": "#EF4444",
  "registration": "#0EA5E9",
  "registration:reminder": "#EAB308",
  "deferment:preparing": "#8B5CF6",
  "deferment:submitted": "#6366F1",
  "pickup:reminder": "#8B5CF6",
  "commission:pending": "#F59E0B",
  "completed successfully": "#059669",
  "to be archived": "#94A3B8",
  "cooperation done": "#10B981",
};

function StudentPipelineWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { isNewTheme, accentColor } = useAppTheme();

  const total = students?.length ?? 0;

  const counts = (students ?? []).reduce<Record<string, number>>((acc, s) => {
    if (s.status) acc[s.status] = (acc[s.status] ?? 0) + 1;
    return acc;
  }, {});

  const sorted = Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  return (
    <Card
      sx={
        isNewTheme
          ? {
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              transition: "box-shadow 0.15s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
            }
          : {
              width: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
            }
      }
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
            Student Pipeline
          </Typography>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
          <Link to="/students" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.3,
                color: "primary.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                opacity: 0.85,
                "&:hover": { opacity: 1 },
              }}
            >
              View all
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        {total === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}>
            No students yet
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" },
              gap: 1.5,
              maxHeight: 320,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { bgcolor: "#F1F5F9", borderRadius: 999 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
            }}
          >
            {sorted.map(([status, count]) => {
              const barColor = STATUS_BAR_COLORS[status] ?? accentColor;
              const chipColor = (CHIPS_COLOR[status] ?? "default") as any;
              const pct = Math.round((count / total) * 100);

              return (
                <Tooltip key={status} title={`${count} students · ${pct}% of total`} arrow>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid #F1F5F9",
                      bgcolor: "#FAFBFC",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      "&:hover": { borderColor: barColor + "66", boxShadow: `0 0 0 3px ${barColor}18` },
                    }}
                  >
                    {/* Color accent bar */}
                    <Box
                      sx={{
                        width: "100%",
                        height: 4,
                        borderRadius: 999,
                        bgcolor: "#E2E8F0",
                        mb: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${pct}%`,
                          height: "100%",
                          bgcolor: barColor,
                          borderRadius: 999,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </Box>

                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ lineHeight: 1, color: "#0F172A", mb: 0.5 }}
                    >
                      {count}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.68rem",
                      }}
                    >
                      {status}
                    </Typography>

                    <Typography variant="caption" sx={{ color: barColor, fontWeight: 700, fontSize: "0.65rem" }}>
                      {pct}%
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default StudentPipelineWidget;
